import * as React from 'react';
import { Grid2 as Grid, Card, CardContent, Tab, Tabs, TabProps } from '@mui/material';
import { ActionRequest } from '../../actions';
import { Loading, LineChart } from '../common';
import { Moment } from 'moment';
import { styled } from '@mui/styles';
import { AxisValueFormatterContext } from '@mui/x-charts/models/axis';
import moment from 'moment/moment';
import { getDifferenceText, truncateNumber } from '../../utils';
import { ReportFilterDates } from './ReportFilterDates';

interface Props {
    readonly styled: StyledProps;
}

type ReportType = 'ActiveUsers' | 'NewUsers' | 'ReturningUsers';
interface State {
    readonly loading: boolean;
    readonly startTimeSpam: Moment;
    readonly endTimeSpam: Moment;
    readonly loadingReport: boolean;
    readonly reportType: ReportType;
    readonly usersReport?: AnalyticsModels.UsersReport;
    readonly currentData?: AnalyticsModels.DayValueReport[];
    readonly precedingData?: AnalyticsModels.DayValueReport[];
}

export class UsersByDayReport extends React.PureComponent<Props, State> {
    state: State = {
        loading: true,
        startTimeSpam: moment.utc().subtract(7, 'days'),
        endTimeSpam: moment.utc(),
        loadingReport: true,
        reportType: 'ActiveUsers',
    };

    componentDidMount() {
        this.getInitialData().then();
    }

    render() {
        const { styled } = this.props;
        const { loading, startTimeSpam, endTimeSpam, loadingReport, reportType, usersReport, currentData, precedingData } = this.state;

        const selected = reportType === 'ActiveUsers' ? 0 : reportType === 'NewUsers' ? 1 : 2;

        const changeSubTab = (event: any, value: any) => {
            switch (value) {
                case 0:
                    this.setState({ reportType: 'ActiveUsers' });
                    this.getActiveUsersDayData().then();
                    break;
                case 1:
                    this.setState({ reportType: 'NewUsers' });
                    this.getNewUsersDayData().then();
                    break;
                case 2:
                    this.setState({ reportType: 'ReturningUsers' });
                    this.getReturningUsersDayData().then();
                    break;
            }
        };

        if (loading) {
            return (
                <Card style={{ padding: 24 }}>
                    <div style={{ height: 352 }}>
                        <Loading styled={styled} />
                    </div>
                </Card>
            );
        }

        if (!usersReport) {
            return (
                <Card style={{ padding: 24 }}>
                    <div style={{ height: 352 }}>
                        <Loading styled={styled} />
                    </div>
                </Card>
            );
        }

        const onFilterDates = (start: Moment, end: Moment) => {
            this.setState({ startTimeSpam: start, endTimeSpam: end});
            this.getInitialData(start, end).then();
        };

        const daysDiff = endTimeSpam.diff(startTimeSpam, 'days') + 1;

        return (
            <Card style={{ padding: 0 }}>
                <CardContent style={{ padding: '0px 10px' }}>
                    <Grid container={true} justifyContent="center" spacing={0}>
                        <Grid size={12}>
                            <StyledTabs
                                value={selected}
                                onChange={changeSubTab}
                                indicatorColor="primary"
                                textColor="primary"
                                variant="scrollable"
                                allowScrollButtonsMobile={true}
                                scrollButtons="auto"
                            >
                                <StyledTab
                                    styled={styled}
                                    title={'Active Users'}
                                    subTitle={`${truncateNumber(usersReport.current.activeUsers)}`}
                                    difference={getDifferenceText(usersReport.current.activeUsers, usersReport.preceding.activeUsers)}
                                    value={0}
                                />
                                <StyledTab
                                    styled={styled}
                                    title={'New Users'}
                                    subTitle={`${truncateNumber(usersReport.current.newUsers)}`}
                                    difference={getDifferenceText(usersReport.current.newUsers, usersReport.preceding.newUsers)}
                                    value={1}
                                />
                                <StyledTab
                                    styled={styled}
                                    title={'Returning Users'}
                                    subTitle={`${truncateNumber(usersReport.current.returningUsers)}`}
                                    difference={getDifferenceText(usersReport.current.returningUsers, usersReport.preceding.returningUsers)}
                                    value={2}
                                />
                            </StyledTabs>
                        </Grid>
                        <Grid size={12}>
                            {loadingReport && !currentData && !precedingData && (
                                <div style={{ height: 300 }}>
                                    <Loading styled={styled} />
                                </div>
                            )}
                            {!loadingReport && currentData && precedingData && (
                                <LineChart
                                    styled={styled}
                                    series={[
                                        {
                                            curve: 'linear',
                                            data: currentData.map(d => d.value),
                                            color: '#1973e8',
                                            id: 'current',
                                            label: 'Current Period',
                                        },
                                        {
                                            curve: 'linear',
                                            data: precedingData.map(d => d.value),
                                            color: '#1973e8',
                                            id: 'preceding',
                                            label: 'Preceding Period',
                                        },
                                    ]}
                                    xAxis={[
                                        {
                                            scaleType: 'point',
                                            data: currentData.map(d => d.day),
                                            valueFormatter: (value: string, context: AxisValueFormatterContext) =>
                                                context.location === 'tick' ? moment(value).format('MMM D') :
                                                    `${moment(value).format('MMM D')} vs ${moment(value).subtract(daysDiff, 'days').format('MMM D')}`
                                            ,
                                        }
                                    ]}
                                    yAxis={[
                                        {
                                            valueFormatter: (value: number, context: AxisValueFormatterContext) => `${truncateNumber(value)}`,
                                            min: 0,
                                        }
                                    ]}
                                    height={300}
                                    grid={{ horizontal: true }}
                                    slotProps={{
                                        legend: {
                                            hidden: true,
                                        }
                                    }}
                                    sx={{
                                        '& .MuiChartsAxis-tickLabel': {
                                            fill: styled.themeMode === 'light' ? '#7d8085' : '#a7aaae !important',
                                        },
                                        '& .MuiChartsAxis-tick': {
                                            display: 'none',
                                        },
                                        '& .MuiChartsAxis-line': {
                                            display: 'none',
                                        },
                                        '& .MuiChartsGrid-line': {
                                            stroke: styled.themeMode === 'light' ? '#eeeeee' : '#303030 !important',
                                        },
                                        '.MuiLineElement-series-preceding': {
                                            strokeDasharray: '5 5',
                                            strokeWidth: 1,
                                        },
                                    }}
                                />
                            )}
                        </Grid>
                        <Grid size={12}>
                            <ReportFilterDates
                                styled={this.props.styled}
                                start={startTimeSpam}
                                end={endTimeSpam}
                                onFilter={onFilterDates}
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>
        );
    }

    private async getInitialData(start?: Moment, end?: Moment): Promise<void> {
        await Promise.all([
            this.getUserData(start, end),
            this.getActiveUsersDayData(start, end)
        ]);
    }

    private async getUserData(start?: Moment, end?: Moment): Promise<void> {
        this.setState({
            loading: true,
            usersReport: undefined,
            currentData: undefined,
            precedingData: undefined,
        });
        const startTimeSpam = start || this.state.startTimeSpam;
        const endTimeSpam = end || this.state.endTimeSpam;
        const url = `analytics/usersReport`;
        const usersReport = await ActionRequest.get<AnalyticsModels.UsersReport>(
            url,
            {
                startDate: startTimeSpam.format('YYYY-MM-DD'),
                endDate: endTimeSpam.format('YYYY-MM-DD'),
            }
        );
        this.setState({
            loading: false,
            usersReport: usersReport || undefined,
        });
    }

    private async getActiveUsersDayData(start?: Moment, end?: Moment): Promise<void> {
        this.setState({
            loadingReport: true,
            currentData: undefined,
            precedingData: undefined,
        });
        const startTimeSpam = start || this.state.startTimeSpam;
        const endTimeSpam = end || this.state.endTimeSpam;
        const url = `analytics/activeUsersDayReport`;
        const data = await ActionRequest.get<AnalyticsModels.DayValueDoubleReport>(
            url,
            {
                startDate: startTimeSpam.format('YYYY-MM-DD'),
                endDate: endTimeSpam.format('YYYY-MM-DD'),
            }
        );
        this.setState({
            loadingReport: false,
            currentData: data?.current || undefined,
            precedingData: data?.preceding || undefined,
        });
    }

    private async getNewUsersDayData(): Promise<void> {
        this.setState({
            loadingReport: true,
            currentData: undefined,
            precedingData: undefined,
        });
        const { startTimeSpam, endTimeSpam } = this.state;
        const url = `analytics/newUsersDayReport`;
        const data = await ActionRequest.get<AnalyticsModels.DayValueDoubleReport>(
            url,
            {
                startDate: startTimeSpam.format('YYYY-MM-DD'),
                endDate: endTimeSpam.format('YYYY-MM-DD'),
            }
        );
        this.setState({
            loadingReport: false,
            currentData: data?.current || undefined,
            precedingData: data?.preceding || undefined,
        });
    }

    private async getReturningUsersDayData(): Promise<void> {
        this.setState({
            loadingReport: true,
            currentData: undefined,
            precedingData: undefined,
        });
        const { startTimeSpam, endTimeSpam } = this.state;
        const url = `analytics/returningUsersDayReport`;
        const data = await ActionRequest.get<AnalyticsModels.DayValueDoubleReport>(
            url,
            {
                startDate: startTimeSpam.format('YYYY-MM-DD'),
                endDate: endTimeSpam.format('YYYY-MM-DD'),
            }
        );
        this.setState({
            loadingReport: false,
            currentData: data?.current || undefined,
            precedingData: data?.preceding || undefined,
        });
    }
}

const StyledTabs = styled(Tabs)(({
    padding: '0px 5px',
    height: 80,
    borderTop: 'none',
    borderBottom: 'none',
}));

interface StyledTabProps extends TabProps {
    readonly styled: StyledProps;
    readonly title: string;
    readonly subTitle: string;
}
interface StyledTabProps extends TabProps {
    readonly styled: StyledProps;
    readonly title: string;
    readonly subTitle: string;
    readonly difference: React.ReactNode;
}
const StyledTab = styled((props: StyledTabProps) => (
    <Tab
        {...props}
        label={(
            <div>
                <div>
                    {props.title}
                </div>
                <div style={{ textAlign: 'left', fontWeight: 400, fontSize: 22, color: props.styled.themeMode === 'light' ? '#202124' : 'white' }}>
                    {props.subTitle}
                </div>
                <div style={{ textAlign: 'left', fontSize: 12 }}>
                    {props.difference}
                </div>
            </div>
        )}
        sx={{
            height: 78,
            fontSize: 14,
            padding: '4px 10px',
            margin: '0px 10px',
            '&.MuiTab-root': {
                color: props.styled.themeMode === 'light' ? '#5f6368' : '#8a8e93',
                '&:hover': {
                    backgroundColor: props.styled.themeMode === 'light' ? '#f1f2f3' : '#292b2c',
                }
            },
            '&.Mui-selected': {
                color: props.styled.themeMode === 'light' ? '#1a73e8' : '#6ba5ff',
                borderTop: props.styled.themeMode === 'light' ? '4px solid #1a73e8' : '4px solid #6ba5ff',
                borderBottom: 'none',
                '&:hover': {
                    backgroundColor: props.styled.themeMode === 'light' ? '#f1f4f9' : '#25282f',
                }
            }
        }}
    >
        {props.children}
    </Tab>
))(() => ({}));